import { css } from 'aphrodite';
import { toast } from 'sonner';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ClientSite, SitemapNode, SitePendingChanges } from '../../../extViewmodels/sdk/models/Website';
import { useDraftPreviewPageQuery } from '../../../queries/Website/useClientSiteQuery';
import { Button } from '../../components-v2/Button';
import { Field, FieldLabel } from '../../components-v2/Field';
import { Input } from '../../components-v2/Input';
import { Popover, PopoverAnchor, PopoverContent } from '../../components-v2/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components-v2/Select';
import { TabToggle } from '../../components-v2/TabToggle';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
	CLEAR_SELECTION_MESSAGE_TYPE,
	ELEMENT_GONE_MESSAGE_TYPE,
	ELEMENT_MOVED_MESSAGE_TYPE,
	ELEMENT_SELECTED_MESSAGE_TYPE,
	ELEMENT_UNAVAILABLE_MESSAGE_TYPE,
	isElementIndex,
	isFieldPath,
	isRect,
	isSelectionMessage,
	NAVIGATE_MESSAGE_TYPE,
	asFrameMessage,
	type PreviewElementSelection,
	SCROLL_TO_MESSAGE_TYPE,
	SCROLLED_MESSAGE_TYPE,
	SELECT_MODE_MESSAGE_TYPE,
} from './previewMessages';
import { THEME } from '../../components-v2/Theme/theme';
import { styleSheet as s } from './styles';

export type PreviewViewport = 'desktop' | 'tablet' | 'mobile';

// Tablet and mobile are real device widths, so desktop is one too. Filling the pane instead would render the
// site at whatever the chat column leaves over, which is never a width anyone's visitor uses.
const VIEWPORT_STYLES: Record<PreviewViewport, React.CSSProperties> = {
	desktop: { width: '100%', minWidth: THEME.breakpoints.xl },
	tablet: { width: 820 },
	mobile: { width: 390 },
};

const VIEWPORT_ITEMS: { value: PreviewViewport; label: string }[] = [
	{ value: 'desktop', label: 'Desktop' },
	{ value: 'tablet', label: 'Tablet' },
	{ value: 'mobile', label: 'Mobile' },
];

interface Props {
	site: ClientSite;
	sitemap?: SitemapNode[];
	pendingChanges?: SitePendingChanges;
	/** Root-relative page path to show, e.g. `/services/roofing`. */
	pagePath: string;
	onPagePathChange: (path: string) => void;
	/** Bumped by the page after every agent change so the frame refetches. */
	reloadKey: number;
	viewport: PreviewViewport;
	onViewportChange: (viewport: PreviewViewport) => void;
	canSelectElements: boolean;
	/** False when the turn couldn't be sent, so the popover keeps the selection and what the reader typed. */
	onElementSubmit: (selection: PreviewElementSelection, text: string) => boolean;
}

/**
 * Read-only frame on the account's unpublished site. The page document is fetched under the app's own bearer
 * token and shown as `srcdoc`, so the frame never authenticates on its own. Nothing is edited inside it — every
 * change comes through the chat.
 *
 * Element picking is hit-tested inside the frame because an element's position in the rendered tree can't be
 * mapped back to its index in the page's element list: grouped elements share a wrapper, some render no markup
 * where they sit, and zone routing reorders them.
 */
export function WebsitePreview({
	site,
	sitemap,
	pendingChanges,
	pagePath,
	onPagePathChange,
	reloadKey,
	viewport,
	onViewportChange,
	canSelectElements,
	onElementSubmit,
}: Props) {
	const frameRef = useRef<HTMLIFrameElement>(null);
	const selectModeButtonRef = useRef<HTMLButtonElement>(null);
	const describeInputId = useId();
	const pageQuery = useDraftPreviewPageQuery({ pagePath, reloadKey });

	// Scroll restore: the frame reports where the reader is; when the same page reloads after an agent edit the
	// new document is told to go back there. A different page starts at the top like any navigation would.
	const lastScrollY = useRef(0);
	const lastLoadedPath = useRef<string | null>(null);

	const [selectMode, setSelectMode] = useState(false);
	const [selection, setSelection] = useState<PreviewElementSelection | null>(null);
	const [draft, setDraft] = useState('');

	const postToFrame = useCallback((message: Record<string, unknown>) => {
		frameRef.current?.contentWindow?.postMessage(message, '*');
	}, []);

	const clearSelection = useCallback(() => {
		setSelection(null);
		setDraft('');
		postToFrame({ type: CLEAR_SELECTION_MESSAGE_TYPE });
	}, [postToFrame]);

	// Picking is the frame's mode, not ours, so it has to come off with the access that allowed it — otherwise a
	// revoked account keeps a live picker with no toggle left to turn it off.
	useEffect(() => {
		if (!canSelectElements) {
			setSelectMode(false);
		}
	}, [canSelectElements]);

	const isPicking = selectMode && canSelectElements;

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			const frameWindow = frameRef.current?.contentWindow;
			if (!frameWindow || event.source !== frameWindow) {
				return;
			}
			const data = asFrameMessage(event.data);
			if (data?.type === SCROLLED_MESSAGE_TYPE && typeof data.y === 'number') {
				lastScrollY.current = data.y;
				return;
			}
			if (data?.type === ELEMENT_SELECTED_MESSAGE_TYPE) {
				if (isPicking && isSelectionMessage(data)) {
					setSelection({
						elementIndex: data.elementIndex,
						label: data.label,
						rect: data.rect,
						fieldPath: isFieldPath(data.fieldPath) ? data.fieldPath : undefined,
						fieldLabel: typeof data.fieldLabel === 'string' ? data.fieldLabel : undefined,
					});
					setDraft('');
				}
				return;
			}
			// The frame leaves any open selection alone on a miss, and so do we — the reader's typed change outlives a
			// stray click.
			if (data?.type === ELEMENT_UNAVAILABLE_MESSAGE_TYPE) {
				if (isPicking) {
					toast.warning('That part of the page isn’t editable here.', { id: ELEMENT_UNAVAILABLE_MESSAGE_TYPE });
				}
				return;
			}
			if (data?.type === ELEMENT_GONE_MESSAGE_TYPE && isElementIndex(data.elementIndex)) {
				const goneIndex = data.elementIndex;
				setSelection(prev => (prev?.elementIndex === goneIndex ? null : prev));
				return;
			}
			if (data?.type === ELEMENT_MOVED_MESSAGE_TYPE && isElementIndex(data.elementIndex) && isRect(data.rect)) {
				const { elementIndex, rect } = data;
				setSelection(prev => (prev?.elementIndex === elementIndex ? { ...prev, rect } : prev));
				return;
			}
			if (data?.type !== NAVIGATE_MESSAGE_TYPE || typeof data.path !== 'string') {
				return;
			}
			// Resolve like the live host would: a relative link on /services/roofing points into /services/. The
			// base is the page actually on screen, which trails pagePath while the next one is still loading. The
			// document is the customer's own markup, so an unparseable href is theirs to have and ours to ignore.
			try {
				const base = lastLoadedPath.current ?? pagePath;
				const resolved = new URL(data.path, `https://preview.invalid${base}`).pathname;
				onPagePathChange(resolved.replace(/\/+$/, '') || '/');
			} catch {
				// no navigation
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [onPagePathChange, pagePath, isPicking]);

	useEffect(() => {
		postToFrame({ type: SELECT_MODE_MESSAGE_TYPE, enabled: isPicking });
		if (!isPicking) {
			clearSelection();
		}
	}, [isPicking, postToFrame, clearSelection]);

	// An index addresses the page as it was rendered, so a page change or an agent edit that re-renders it
	// invalidates the selection before the new document has even loaded.
	useEffect(() => {
		clearSelection();
	}, [pagePath, reloadKey, clearSelection]);

	const handleFrameLoad = useCallback(() => {
		const samePage = lastLoadedPath.current === pagePath;
		lastLoadedPath.current = pagePath;
		if (samePage && lastScrollY.current > 0) {
			postToFrame({ type: SCROLL_TO_MESSAGE_TYPE, y: lastScrollY.current });
		} else {
			lastScrollY.current = 0;
		}
		// The reload replaced the document, so the mode the toggle is showing has to be re-applied to it.
		postToFrame({ type: SELECT_MODE_MESSAGE_TYPE, enabled: isPicking });
		clearSelection();
	}, [pagePath, isPicking, postToFrame, clearSelection]);

	const handleSelectionSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const text = draft.trim();
		if (!selection || !text) {
			return;
		}
		if (onElementSubmit(selection, text)) {
			clearSelection();
		}
	};

	const pendingCount = pendingChanges?.changes?.length ?? 0;
	const currentNode = sitemap?.find(n => n.resolvedUrl === pagePath);
	const selectionTitle = selection ? (selection.fieldLabel ?? selection.label) : '';

	return (
		<div className={css(s.previewPane)}>
			<div className={css(s.toolbar)}>
				<div className={css(s.toolbarTitle)}>{site.siteTitle || 'Your website'}</div>
				{sitemap?.length ? (
					<Select value={currentNode?.resolvedUrl ?? pagePath} onValueChange={onPagePathChange}>
						<SelectTrigger styles={s.pageSelect} aria-label='Page'>
							<span className={css(s.pageSelectLabel)}>{sitemapNodeLabel(currentNode) || pagePath}</span>
						</SelectTrigger>
						<SelectContent>
							{sitemap.map(node => (
								<SelectItem key={node.pageId} value={node.resolvedUrl}>
									{sitemapNodeLabel(node)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : null}
				<TabToggle
					items={VIEWPORT_ITEMS}
					value={viewport}
					onValueChange={v => onViewportChange(v as PreviewViewport)}
					aria-label='Preview size'
				/>
				{canSelectElements ? (
					<Button
						ref={selectModeButtonRef}
						variant='secondary'
						size='lg'
						onClick={() => setSelectMode(v => !v)}
						aria-pressed={selectMode}
					>
						{selectMode ? 'Cancel' : 'Edit'}
					</Button>
				) : null}
				{site.isPublished && isWebUrl(site.publishedUrl) ? (
					<a className={css(s.openLink)} href={site.publishedUrl} target='_blank' rel='noreferrer'>
						Open live site
					</a>
				) : null}
			</div>

			{site.access.locked ? (
				<div className={css(s.banner, s.lockBanner)}>
					{site.access.lockNote || "Our team is updating your site right now — editing reopens when they're done."}
				</div>
			) : null}
			{isPicking ? (
				<div className={css(s.banner, s.pickingBanner)} role='status' aria-live='polite'>
					Click any text or image on the page to change it. Menus, headers and footers belong to the site layout, so
					they can’t be picked here.
				</div>
			) : null}
			{pendingCount > 0 ? (
				<div className={css(s.banner, s.pendingBanner)} role='status' aria-live='polite'>
					{pendingCount === 1 ? '1 change is' : `${pendingCount} changes are`} not live yet.{' '}
					{site.access.publish
						? 'Ask Lev to publish when you’re ready.'
						: 'The Levitate team publishes changes for you.'}
				</div>
			) : null}

			<div className={css(s.frameHost)}>
				{pageQuery.data ? (
					<div className={css(s.frameWrap)} style={VIEWPORT_STYLES[viewport]}>
						{/* No allow-same-origin: the document is the customer's own markup (html_block can carry script), and
						srcdoc without it runs as an opaque origin that can't reach the app. postMessage still works. */}
						<iframe
							ref={frameRef}
							className={css(s.frame)}
							srcDoc={pageQuery.data.html}
							onLoad={handleFrameLoad}
							title='Website preview'
							sandbox='allow-scripts allow-popups'
						/>
						<Popover open={Boolean(selection)} onOpenChange={open => !open && clearSelection()}>
							{selection ? (
								<PopoverAnchor asChild>
									<div
										className={css(s.selectionAnchor)}
										style={{
											left: selection.rect.left,
											top: selection.rect.top,
											width: selection.rect.width,
											height: selection.rect.height,
										}}
									/>
								</PopoverAnchor>
							) : null}
							<PopoverContent
								side='bottom'
								align='start'
								sideOffset={8}
								aria-labelledby={`${describeInputId}-label`}
								// Anchored rather than triggered, so Radix has no trigger to restore focus to and would
								// otherwise strand it on the body.
								onCloseAutoFocus={e => {
									e.preventDefault();
									selectModeButtonRef.current?.focus();
								}}
							>
								<form className={css(s.selectionForm)} onSubmit={handleSelectionSubmit}>
									<Field>
										<FieldLabel htmlFor={describeInputId} id={`${describeInputId}-label`}>
											{selectionTitle}
										</FieldLabel>
										<Input
											id={describeInputId}
											autoFocus
											value={draft}
											onChange={e => setDraft(e.target.value)}
											placeholder='Describe the change…'
										/>
									</Field>
									<div className={css(s.selectionActions)}>
										<Button type='button' variant='ghost' size='sm' onClick={clearSelection}>
											Cancel
										</Button>
										<Button type='submit' size='sm' disabled={!draft.trim()}>
											Send to Lev
										</Button>
									</div>
								</form>
							</PopoverContent>
						</Popover>
					</div>
				) : pageQuery.isError ? (
					<div className={css(s.banner)} role='alert'>
						This page couldn’t be previewed right now.
					</div>
				) : (
					<LoadingSpinner />
				)}

				{pageQuery.data && pageQuery.isFetching ? (
					<div className={css(s.frameLoading)}>
						<LoadingSpinner />
					</div>
				) : null}
			</div>
		</div>
	);
}

function isWebUrl(value?: string): boolean {
	if (!value) {
		return false;
	}
	try {
		return ['http:', 'https:'].includes(new URL(value).protocol);
	} catch {
		return false;
	}
}

function sitemapNodeLabel(node?: SitemapNode): string {
	return node ? node.pageName || node.slug || node.resolvedUrl : '';
}
